import { useCallback, useEffect, useMemo, useState } from 'react';
import app from 'config/app.json';
import { getUrlParameter } from 'utils/urlUtils';
import { useIntl } from 'react-intl';
import { useNotification } from 'hook/useNotification';
import { fErrors } from 'utils/formatError';
import { getDeepNestedFieldValue } from 'utils/entityUtils';
import { useQueryData } from 'hook/useQueryData';
import { useGetDefaultConfigurationQuery } from 'services/applicationApi';

export const AccessorType = Object.freeze({
  ExtraParameter: 'extraParameter',
  File: 'file',
  Parameter: 'parameter',
  EntityProperty: 'property',
  Property: 'baseProperty',
  Action: 'action',
  Select: 'select'
});

export const PositionType = Object.freeze({ Top: 'top', Bottom: 'bottom' });
export const TaskType = Object.freeze({ Anonymization: 'anonymization' });

const useConf = (configurationRetriever) => {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const currentAppCode = getUrlParameter(window.location, app.url.paramApp);

  const [defaultAppConfLoading, setDefaultAppConfLoading] = useState(true);
  const [currentAppConfLoading, setCurrentAppConfLoading] = useState(true);

  const {
    data: defaultConf,
    loading: isDefaultConfLoading,
    error: defaultConfError,
    refetch: refetchDefaultConf
  } = useQueryData(useGetDefaultConfigurationQuery);

  useEffect(() => {
    setDefaultAppConfLoading(isDefaultConfLoading);
    if (defaultConfError) {
      console.error(
        `Error occurred when retrieving default configuration: ${JSON.stringify(defaultConfError)}`
      );
      try {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: fErrors(msg, defaultConfError)
        });
      } catch (e) {
        // Ignore exception
      }
    }
  }, [addNotification, defaultConfError, isDefaultConfLoading, msg]);

  const {
    data: currentConf,
    isLoading: isCurrentConfLoading,
    isFetching: isCurrentConfFetching,
    error: currentConfError,
    refetch: refetchCurrentConf
  } = configurationRetriever({ appCode: currentAppCode ?? app.application.defaultCode });

  useEffect(() => {
    setCurrentAppConfLoading(isCurrentConfFetching || isCurrentConfLoading);
    if (currentConfError) {
      console.error(
        `Error occurred when retrieving current configuration: ${JSON.stringify(currentConfError)}`
      );
      try {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: fErrors(msg, currentConfError)
        });
      } catch (e) {
        // Ignore exception
      }
    }
  }, [addNotification, currentConfError, isCurrentConfFetching, isCurrentConfLoading, msg]);

  const getValue = useCallback(
    (code, defaultValue = null) => {
      if (!code) {
        return defaultValue;
      }
      // Searchs in current conf
      let res = getDeepNestedFieldValue(code, currentConf);
      if (res === null) {
        res = getDeepNestedFieldValue(code, defaultConf);
      }
      return res !== null ? res : defaultValue;
    },
    [currentConf, defaultConf]
  );

  const views = useMemo(
    () =>
      [...new Set(Object.keys(defaultConf || []).concat(Object.keys(currentConf || [])))].filter(
        (value) => !value.startsWith('//')
      ),
    [currentConf, defaultConf]
  );

  const refetch = () => {
    refetchDefaultConf();
    refetchCurrentConf();
  };

  return {
    isLoading: defaultAppConfLoading || currentAppConfLoading,
    error: defaultConfError || currentConfError,
    views,
    getValue,
    refetch
  };
};

export default useConf;
